// Phone bottom tab bar (<768 px): the 5 first pages the person may open, by
// the owner's priority (WhatsApp · Orders · Scanner · Printing · AMEEX), then
// « More ». Sits on the home-bar safe area; its full height is --adm-bottom-h.
// Hidden while typing (html[data-adm-typing], see index.css) like a native app.
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import { activeNavPath, phoneTabs, ADMIN_NAV, type Access } from '@/lib/appShell/navModel';
import { NavIcon, MoreIcon } from './navIcons';
import { ShellLink } from './ShellLink';

const GOLD = 'var(--color-adm-gold-deep)';

export function PhoneTabBar({ access, pathname, moreOpen, onMore }: {
  access: Access; pathname: string; moreOpen: boolean; onMore: () => void;
}) {
  const { t } = useT();
  const tabs = phoneTabs(access);
  const active = activeNavPath(pathname, ADMIN_NAV);
  const onTab = tabs.some((x) => x.to === active);
  const moreActive = moreOpen || !onTab;

  const cell = (on: boolean) => cn(
    'relative flex-1 min-w-0 min-h-[52px] flex flex-col items-center justify-center gap-[3px] select-none',
    'transition-colors duration-150 active:bg-[rgba(201,162,74,0.10)]',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#C9A24A]/60',
    on ? 'text-adm-gold-ink' : 'text-gray-500',
  );
  const label = 'max-w-full truncate text-[10px] leading-none tracking-[-0.01em] font-medium';
  const pip = (on: boolean) => on && (
    <span aria-hidden className="absolute top-0 left-1/2 -translate-x-1/2 h-[3px] w-7 rounded-b-full" style={{ background: '#C9A24A' }} />
  );

  return (
    <nav
      aria-label={t('Navigation principale')}
      className="adm-tabbar md:hidden fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur-md"
      // A shadow line, not a border: the bar is exactly 56 px + the inset (--adm-bottom-h).
      style={{ boxShadow: '0 -1px 0 rgba(201,162,74,0.22)', paddingBottom: 'var(--adm-safe-bottom)' }}
    >
      <div className="flex items-stretch h-14 px-1">
        {tabs.map((tab) => {
          const on = active === tab.to && !moreOpen;
          return (
            <ShellLink key={tab.to} to={tab.to} data-tab aria-current={on ? 'page' : undefined} className={cell(on)}>
              {pip(on)}
              <span aria-hidden style={{ color: on ? GOLD : undefined }}><NavIcon to={tab.to} size={22} /></span>
              <span className={label}>{t(tab.tabLabel ?? tab.label)}</span>
            </ShellLink>
          );
        })}
        <button
          type="button"
          data-tab
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          aria-current={moreActive && !moreOpen ? 'page' : undefined}
          onClick={onMore}
          className={cell(moreActive)}
        >
          {pip(moreActive)}
          <span aria-hidden style={{ color: moreActive ? GOLD : undefined }}><MoreIcon /></span>
          <span className={label}>{t('Plus')}</span>
        </button>
      </div>
    </nav>
  );
}
